// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    priceAndActions?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    priceAvailabilityLink?: InjectableCss;
    minimumOrderQty?: InjectableCss;
    multipleSaleQty?: InjectableCss;
}

const productGridContainerStyle: ProductGridContainerStyle = {
    container: {
        css: css`
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            max-width: 316px;
            width: 100%;
            border-radius: 0;
            background-color: transparent;
            box-shadow: none;
            box-sizing: border-box;

            ${xScreen("lte", "md")} {
                max-width: 360px;
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            margin-bottom: 12px;
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
        `,
    },
    priceAndActions: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: auto;
            height: 100%;
        `,
    },
    price: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    availability: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    addToCartActions: {
        css: css`
            display: flex;
            margin-top: auto;
        `,
    },
    addToList: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps}>
                {children}
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector="productCarousel-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

const PriceAndActions: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceAndActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...priceAndActions} className="product-grid-container-priceAndActions">
            {children}
        </StyledWrapper>
    );
};

const PriceAvailabilityLink: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceAvailabilityLink },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper
            {...otherProps}
            {...priceAvailabilityLink}
            data-test-selector="productCarousel-PriceAvailabilityLink"
        >
            {children}
        </StyledWrapper>
    );
};

const Price: FC = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability}>
            {children}
        </StyledWrapper>
    );
};

const MinimumOrderQty: FC = ({ children, ...otherProps }) => {
    const {
        styles: { minimumOrderQty },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...minimumOrderQty}>
            {children}
        </StyledWrapper>
    );
};

const MultipleSaleQty: FC = ({ children, ...otherProps }) => {
    const {
        styles: { multipleSaleQty },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...multipleSaleQty}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Info = Info;
ProductGridContainer.PriceAndActions = PriceAndActions;
ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.Price = Price;
ProductGridContainer.Availability = Availability;
ProductGridContainer.AddToList = AddToList;
ProductGridContainer.PriceAvailabilityLink = PriceAvailabilityLink;
ProductGridContainer.MinimumOrderQty = MinimumOrderQty;
ProductGridContainer.MultipleSaleQty = MultipleSaleQty;

export default ProductGridContainer;
